<template>
  <div
    class="flex flex-col justify-center mb-2 bg-white rounded-xl border border-gray-300 border-solid"
  >
    <div class="flex flex-col justify-center w-full rounded-xl">
      <div
        class="flex gap-10 justify-between items-center px-6 py-5 rounded-xl w-full bg-slate-50 cursor-pointer"
        @click="toggleCollapse"
      >
        <div class="flex items-center gap-2">
          <div
            class="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-black font-semibold text-sm"
          >
            4
          </div>
          <h3 class="text-sm font-semibold text-black">Select Experts</h3>
        </div>
        <button
          class="flex justify-between items-center self-stretch my-auto w-5"
          aria-label="Toggle expert selection"
        >
          <div v-html="isCollapsed ? ARROW_DOWN : ARROW_UP"></div>
        </button>
      </div>

      <div
        class="flex flex-col w-full px-6 pb-5 rounded-xl max-md:px-5"
        v-if="!isCollapsed"
        ref="collapsible"
      >
        <div class="flex flex-col mb-4 mt-4 text-left pb-4">
          <!-- Random Experts Toggle -->
          <div
            class="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg"
          >
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900"
                >Pick Random Experts</span
              >
              <span class="text-sm text-gray-500"
                >Let the system randomly assign experts to this layout</span
              >
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="isRandomMode"
                class="sr-only peer"
                @change="handleRandomModeChange"
              />
              <div
                class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-purple/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-purple"
              ></div>
            </label>
          </div>

          <div v-if="!isRandomMode" class="flex flex-col mb-2">
            <BaseLabel
              forAttr="expertSelector"
              :text="`Choose which expert to display in this layout. ${
                layoutType === 'single'
                  ? 'Select one expert for single layout.'
                  : layoutType === 'row'
                  ? 'Select up to three experts for row layout.'
                  : 'All experts will be shown in grid layout.'
              }`"
              class="font-inter text-sm font-medium leading-normal tracking-normal mb-2"
            />

            <!-- Single Expert Selection -->
            <div v-if="layoutType === 'single'" class="flex flex-col">
              <BaseSearchableDropdown
                :options="
                  approvedExperts.map(
                    (e) =>
                      e.firstName +
                      ' ' +
                      e.lastName +
                      (e.title ? ' - ' + e.title : '')
                  )
                "
                :modelValue="selectedExpertName"
                @change="handleSingleExpertChange"
                placeholder="Select expert"
              />
              <!-- Error Message -->
              <div
                v-if="fieldErrors[`${layoutId}-0`]"
                class="mt-2 p-2 bg-red-50 rounded-md"
              >
                <p class="text-sm text-red-600">
                  {{ fieldErrors[`${layoutId}-0`] }}
                </p>
              </div>
              <button
                v-if="selectedExpertIds.length > 0"
                @click="clearAllExperts"
                class="mt-3 px-4 py-2 text-sm text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors"
              >
                Clear All
              </button>
            </div>

            <!-- Multiple Expert Selection for Row Layout -->
            <div v-if="layoutType === 'row'" class="flex flex-col gap-3">
              <div class="flex flex-col">
                <BaseLabel
                  forAttr="expert-1"
                  text="Expert 1"
                  class="font-inter text-sm font-medium leading-normal tracking-normal mb-1"
                />
                <BaseSearchableDropdown
                  :options="
                    approvedExperts.map(
                      (e) =>
                        e.firstName +
                        ' ' +
                        e.lastName +
                        (e.title ? ' - ' + e.title : '')
                    )
                  "
                  :modelValue="getExpertNameById(selectedExpertIds[0] || '')"
                  @change="(name) => handleRowExpertChange(0, name)"
                  placeholder="Select expert 1"
                />
                <!-- Error Message -->
                <div
                  v-if="fieldErrors[`${layoutId}-0`]"
                  class="mt-2 p-2 bg-red-50 rounded-md"
                >
                  <p class="text-sm text-red-600">
                    {{ fieldErrors[`${layoutId}-0`] }}
                  </p>
                </div>
              </div>

              <div class="flex flex-col">
                <BaseLabel
                  forAttr="expert-2"
                  text="Expert 2"
                  class="font-inter text-sm font-medium leading-normal tracking-normal mb-1"
                />
                <BaseSearchableDropdown
                  :options="
                    approvedExperts.map(
                      (e) =>
                        e.firstName +
                        ' ' +
                        e.lastName +
                        (e.title ? ' - ' + e.title : '')
                    )
                  "
                  :modelValue="getExpertNameById(selectedExpertIds[1] || '')"
                  @change="(name) => handleRowExpertChange(1, name)"
                  placeholder="Select expert 2"
                />
                <!-- Error Message -->
                <div
                  v-if="fieldErrors[`${layoutId}-1`]"
                  class="mt-2 p-2 bg-red-50 rounded-md"
                >
                  <p class="text-sm text-red-600">
                    {{ fieldErrors[`${layoutId}-1`] }}
                  </p>
                </div>
              </div>

              <div class="flex flex-col">
                <BaseLabel
                  forAttr="expert-3"
                  text="Expert 3"
                  class="font-inter text-sm font-medium leading-normal tracking-normal mb-1"
                />
                <BaseSearchableDropdown
                  :options="
                    approvedExperts.map(
                      (e) =>
                        e.firstName +
                        ' ' +
                        e.lastName +
                        (e.title ? ' - ' + e.title : '')
                    )
                  "
                  :modelValue="getExpertNameById(selectedExpertIds[2] || '')"
                  @change="(name) => handleRowExpertChange(2, name)"
                  placeholder="Select expert 3"
                />
                <!-- Error Message -->
                <div
                  v-if="fieldErrors[`${layoutId}-2`]"
                  class="mt-2 p-2 bg-red-50 rounded-md"
                >
                  <p class="text-sm text-red-600">
                    {{ fieldErrors[`${layoutId}-2`] }}
                  </p>
                </div>
              </div>

              <button
                v-if="selectedExpertIds.length > 0"
                @click="clearAllExperts"
                class="mt-3 px-4 py-2 text-sm text-red-600 border border-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
          <div v-else class="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
            Experts will be randomly assigned to this layout when displayed.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, computed, watch } from "vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import BaseSearchableDropdown from "@/components/shared/BaseSearchableDropdown.vue";
import { ARROW_DOWN, ARROW_UP } from "@/assets/svg/shared/svg-constants";
import { ApprovedExpert } from "@/types/dashboard-types";

export default defineComponent({
  name: "ExpertSelector",
  components: {
    BaseLabel,
    BaseSearchableDropdown,
  },
  props: {
    approvedExperts: {
      type: Array as () => ApprovedExpert[],
      required: true,
    },
    selectedExpertIds: {
      type: Array as () => string[],
      default: () => [],
    },
    layoutType: {
      type: String,
      required: true,
    },
    selectRandomExperts: {
      type: Boolean,
      default: false,
    },
    layoutId: {
      type: String,
      required: true,
    },
    fieldErrors: {
      type: Object as () => Record<string, string>,
      default: () => ({}),
    },
  },
  emits: ["expertChanged", "randomModeChanged"],
  setup(props, { emit }) {
    const isCollapsed = ref(false);
    const isRandomMode = ref(props.selectRandomExperts);

    // Simple initialization - just use the selected expert IDs and fill empty slots
    const localSelectedExpertIds = ref<string[]>([...props.selectedExpertIds]);

    // Watch for prop changes and update local state
    watch(
      () => props.selectedExpertIds,
      (newIds) => {
        localSelectedExpertIds.value = [...newIds];
      },
      { immediate: true }
    );

    // Watch for random mode prop changes
    watch(
      () => props.selectRandomExperts,
      (newValue) => {
        isRandomMode.value = newValue;
      }
    );

    const selectedExpertName = computed(() => {
      if (
        !localSelectedExpertIds.value ||
        localSelectedExpertIds.value.length === 0
      ) {
        return "";
      }
      const selectedExpert = props.approvedExperts.find(
        (e) => localSelectedExpertIds.value[0] === e.id
      );
      return selectedExpert
        ? selectedExpert.firstName +
            " " +
            selectedExpert.lastName +
            (selectedExpert.title ? " - " + selectedExpert.title : "")
        : "";
    });

    const getExpertNameById = (expertId: string): string => {
      if (!expertId || expertId === "") return "";
      const expert = props.approvedExperts.find((e) => e.id === expertId);
      return expert
        ? expert.firstName +
            " " +
            expert.lastName +
            (expert.title ? " - " + expert.title : "")
        : "";
    };

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const handleRandomModeChange = () => {
      if (isRandomMode.value) {
        // Clear selected experts when switching to random mode
        localSelectedExpertIds.value = [];
        emit("expertChanged", []);
      }
      emit("randomModeChanged", isRandomMode.value);
    };

    const handleSingleExpertChange = (name: string) => {
      const selectedExpert = props.approvedExperts.find(
        (e) =>
          e.firstName + " " + e.lastName + (e.title ? " - " + e.title : "") ===
          name
      );
      if (selectedExpert) {
        // Check if this expert is already selected
        if (
          localSelectedExpertIds.value.length > 0 &&
          localSelectedExpertIds.value[0] === selectedExpert.id
        ) {
          // Same expert selected again, do nothing
          return;
        }

        emit("expertChanged", [selectedExpert.id], 0);
      }
    };

    const handleRowExpertChange = (index: number, name: string) => {
      const selectedExpert = props.approvedExperts.find(
        (e) =>
          e.firstName + " " + e.lastName + (e.title ? " - " + e.title : "") ===
          name
      );
      if (selectedExpert) {
        const newIds = [...localSelectedExpertIds.value];
        // Ensure array has enough elements
        while (newIds.length <= index) {
          newIds.push("");
        }

        // Check if this expert is already selected in other positions
        const isDuplicate = newIds.some(
          (id, i) => id === selectedExpert.id && i !== index
        );
        if (isDuplicate) {
          // Don't update local state if it's a duplicate - let the parent handle the error
          emit(
            "expertChanged",
            newIds.map((id, i) => (i === index ? selectedExpert.id : id)),
            index
          );
          return;
        }

        newIds[index] = selectedExpert.id;
        localSelectedExpertIds.value = newIds;
        emit("expertChanged", newIds, index);
      }
    };

    const clearAllExperts = () => {
      localSelectedExpertIds.value = [];
      emit("expertChanged", []);
    };

    return {
      isCollapsed,
      isRandomMode,
      toggleCollapse,
      ARROW_DOWN,
      ARROW_UP,
      handleSingleExpertChange,
      handleRowExpertChange,
      selectedExpertName,
      getExpertNameById,
      localSelectedExpertIds,
      clearAllExperts,
      handleRandomModeChange,
    };
  },
});
</script>
